import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Button, CircularProgress, Alert, Paper, 
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, 
  Grid, FormControl, Select, MenuItem, Chip, Tabs, Tab, InputLabel,
  TextField, Accordion, AccordionSummary, AccordionDetails, Card, CardContent
} from '@mui/material';
import {
  Build, AttachMoney, HelpOutline, CalendarToday,
  Delete, Close as CloseIcon, SupportAgent, PendingActions,
  History, InboxOutlined, CheckCircle, ExpandMore
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import { createTicket, fetchTicketsBySchoolId, updateTicket } from '../help&support/redux/HelpSupportAction';
import { selectSchoolDetails, selectUserActualData, api } from '../../../common';

// ==================== Helper Functions ====================
const getStatusDisplay = (status) => {
  switch (status) {
    case 'OPEN': return 'Open';
    case 'IN_PROGRESS': return 'In Progress';
    case 'RESOLVED': return 'Resolved';
    case 'CLOSED': return 'Closed';
    default: return status;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'OPEN': return 'info.light';
    case 'IN_PROGRESS': return 'warning.light';
    case 'RESOLVED': return 'success.light';
    case 'CLOSED': return 'success.dark';
    default: return 'grey.300';
  }
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ==================== Complaints Table Component ====================
const ComplaintsTable = ({ 
  complaints, 
  isLoading, 
  error, 
  onRefresh, 
  onViewComplaint, 
  onDeleteClick,
  onStatusChange,
  updatingStatus
}) => {
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Complaint Tickets
        </Typography>
        <Button 
          variant="contained" 
          onClick={onRefresh}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : complaints.length > 0 ? (
        <TableContainer component={Paper} elevation={3}>
          <Table sx={{ minWidth: 650 }} aria-label="complaints table">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white' }}>Ticket ID</TableCell>
                <TableCell sx={{ color: 'white' }}>Subject</TableCell>
                <TableCell sx={{ color: 'white' }}>Priority</TableCell>
                <TableCell sx={{ color: 'white' }}>Status</TableCell>
                <TableCell sx={{ color: 'white' }}>Created By</TableCell>
                <TableCell sx={{ color: 'white' }}>Created Date</TableCell>
                <TableCell sx={{ color: 'white' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {complaints.map((ticket) => (
                <TableRow 
                  key={ticket.id}
                  hover
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>{ticket.id}</TableCell>
                  <TableCell sx={{ fontWeight: 'medium' }}>{ticket.subject}</TableCell>
                  <TableCell>
                    <Chip 
                      label={ticket.priority} 
                      color={
                        ticket.priority === 'HIGH' ? 'error' : 
                        ticket.priority === 'MEDIUM' ? 'warning' : 'success'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box 
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        borderRadius: 1,
                        backgroundColor: getStatusColor(ticket.status),
                        color: 'common.white'
                      }}
                    >
                      {getStatusDisplay(ticket.status)}
                    </Box>
                  </TableCell>
                  <TableCell>{ticket.createdBy}</TableCell>
                  <TableCell>
                    {new Date(ticket.createdDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => onViewComplaint(ticket)}
                      >
                        View
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        size="small"
                        onClick={() => onDeleteClick(ticket.id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No complaints found
          </Typography>
          <Button 
            variant="outlined" 
            onClick={onRefresh}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Paper>
      )}
    </Box>
  );
};

// ==================== Complaint Detail Modal ====================
const ComplaintDetailModal = ({ 
  complaint, 
  isOpen, 
  onClose, 
  onStatusChange, 
  onDeleteClick,
  messages,
  isMessagesLoading,
  updatingStatus,
  isAdmin
}) => {
  if (!complaint) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Complaint Details: {complaint.subject}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Typography variant="subtitle2">ID:</Typography>
            <Typography>{complaint.id}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Status:</Typography>
            {isAdmin ? (
              <FormControl size="small" fullWidth>
                <Select
                  value={complaint.status}
                  onChange={(e) => onStatusChange(complaint.id, e.target.value)}
                  disabled={updatingStatus[complaint.id]}
                >
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="RESOLVED">Resolved</MenuItem>
                  <MenuItem value="CLOSED">Closed</MenuItem>
                </Select>
                {updatingStatus[complaint.id] && (
                  <CircularProgress size={24} sx={{ position: 'absolute', right: 40, top: 8 }} />
                )}
              </FormControl>
            ) : (
              <Box 
                sx={{
                  display: 'inline-block',
                  px: 1,
                  borderRadius: 1,
                  backgroundColor: getStatusColor(complaint.status),
                  color: 'common.white'
                }}
              >
                {getStatusDisplay(complaint.status)}
              </Box>
            )}
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="subtitle2">Priority:</Typography>
            <Chip 
              label={complaint.priority} 
              color={
                complaint.priority === 'HIGH' ? 'error' : 
                complaint.priority === 'MEDIUM' ? 'warning' : 'success'
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Assigned To:</Typography>
            <Typography>{complaint.assignedTo || 'Unassigned'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Created By:</Typography>
            <Typography>{complaint.createdBy}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Updated By:</Typography>
            <Typography>{complaint.updatedBy}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Created Date:</Typography>
            <Typography>
              {new Date(complaint.createdDate).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Updated Date:</Typography>
            <Typography>
              {new Date(complaint.updatedDate).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">School ID:</Typography>
            <Typography>{complaint.schoolId}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Session:</Typography>
            <Typography>{complaint.session}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Resolution:</Typography>
            <Typography>{complaint.resolution || 'No resolution provided'}</Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2">Description:</Typography>
          <Typography sx={{ mt: 1 }}>{complaint.description}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => onDeleteClick(complaint.id)}
          color="error"
        >
          Delete Complaint
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== Delete Confirmation Dialog ====================
const DeleteConfirmationDialog = ({ 
  open, 
  onClose, 
  onConfirm, 
  complaintId 
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete complaint #{complaintId}?
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onConfirm} 
          color="error"
          variant="contained"
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== Main HelpSupport Component ====================
const HelpSupport = () => {
  const dispatch = useDispatch();
  const schoolData = useSelector(selectSchoolDetails);
  const userData = useSelector(selectUserActualData);
  const isAdmin = userData?.role === 'super_admin';
  
  // Form state
  const [formData, setFormData] = useState({
    category: 'Technical',
    description: '',
    priority: 'MEDIUM',
    status: 'OPEN'
  });
  
  // Tabs state
  const [tabValue, setTabValue] = useState(0);
  
  // Complaints state
  const [complaints, setComplaints] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
   const [loading, setLooading] = useState(false);

  const [error, setError] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);
  
  // Selected complaint state
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  
  // Status update state
  const [updatingStatus, setUpdatingStatus] = useState({});
  
  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [complaintToDelete, setComplaintToDelete] = useState(null);
  // Snackbar state
  const [snackbar, setSnackbar] = useState(null);
   const [laoading, setLoading] = useState(false);

  // Fetch complaints
  const fetchComplaints = useCallback(async () => {
    try {
      if (!schoolData?.id) return;
      
      setIsLoading(true);
      setError(null);
      
      const response = await api.get(`/api/help/support/school/${schoolData.id}`);
      const data = response.data;
      
      setComplaints(Array.isArray(data) ? data : [data]);
    } catch (err) {
      console.error('Failed to fetch complaints:', err);
      setError(err.message || 'Failed to load complaints');
      setComplaints([]);
    } finally {
      setIsLoading(false);
    }
  }, [schoolData]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchComplaints();
    }
  }, [tabValue, fetchComplaints, refreshCount]);



  // Handle view complaint
  const handleViewComplaint = useCallback((complaint) => {
    setSelectedComplaint(complaint);
    setIsModalOpen(true);
  }, [fetchComplaints]);

  // Handle status change
const handleStatusChange = useCallback(async (id, newStatus) => {
  try {
    setUpdatingStatus(prev => ({ ...prev, [id]: true }));
    
    // Find the current ticket in state
    const currentTicket = complaints.find(ticket => ticket.id === id);
    if (!currentTicket) {
      throw new Error('Ticket not found');
    }

    // Prepare full payload with required fields
    const payload = {
      ...currentTicket,  // Include all existing fields
      status: newStatus.toUpperCase(),
      updatedDate: new Date().toISOString(),
      updatedBy: userData?.tableName || 'system'  // Add current user
    };

    const response = await api.put(
      `/api/help/support/update/${id}`,
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    );
    
    if (response.status !== 200 && response.status !== 204) {
      throw new Error('Failed to update status');
    }
    
    // Update local state
    setComplaints(prev => prev.map(ticket => 
      ticket.id === id ? payload : ticket
    ));
    
    if (selectedComplaint?.id === id) {
      setSelectedComplaint(payload);
    }
    
    setSnackbar({
      open: true,
      message: 'Status updated successfully!',
      severity: 'success'
    });
  } catch (error) {
    console.error('Status update error:', error);
    setSnackbar({
      open: true,
      message: error.response?.data?.message || 
               error.message || 
               'Failed to update status',
      severity: 'error'
    });
  } finally {
    setUpdatingStatus(prev => ({ ...prev, [id]: false }));
  }
}, [complaints, selectedComplaint, userData]);

  // Handle delete click
  const handleDeleteClick = useCallback((id) => {
    setComplaintToDelete(id);
    setDeleteDialogOpen(true);
  }, []);

  // Confirm delete
  const handleConfirmDelete = useCallback(async () => {
    try {
      const response = await api.delete(`/api/help/support/delete/${complaintToDelete}`);
      
      if (!response.ok) {
        throw new Error('Failed to delete complaint');
      }
      
      // Update complaints list
      setComplaints(prev => prev.filter(c => c.id !== complaintToDelete));
      
      // Close modal if deleted complaint is open
      if (selectedComplaint?.id === complaintToDelete) {
        handleCloseModal();
      }
      
      setSnackbar({
        open: true,
        message: 'Complaint deleted successfully!',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Delete error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete complaint',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setComplaintToDelete(null);
    }
  }, [complaintToDelete, selectedComplaint]);

  // Cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setComplaintToDelete(null);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    setRefreshCount(prev => prev + 1);
  }, []);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedComplaint(null);
    setMessages([]);
  }, []);

  // Handle ticket form submit
  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    
    try {
      if (!formData.category) {
        throw new Error('Category is required');
      }
      
      const payload = {
        subject: formData.category,
        description: formData.description,
        status: formData.status,
        priority: formData.priority,
        schoolId: schoolData?.id || 0,
        session: schoolData?.session || '2024-2025',
        createdBy: userData?.tableName || 'Unknown',
        createdDate: new Date().toISOString(),
        updatedDate: new Date().toISOString(),
        updatedBy: userData?.tableName || 'Unknown',
        resolution: "",
        assignedTo: ""
      };
      
      const response = await api.post('/api/help/support/create', payload);
      
    
      
      // Refresh complaints list
      handleRefresh();
      
      // Reset form
      setFormData({
        category: 'Technical',
        description: '',
        priority: 'MEDIUM',
        status: 'OPEN'
      });
      
      setSnackbar({
        open: true,
        message: 'Ticket created successfully!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Submission error:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to submit ticket',
        severity: 'error'
      });
    }
  };

  // FAQ data
  const faqItems = [
    { question: "How do I reset my password?", answer: "Go to Account Settings > Security > Reset Password" },
    { question: "Where can I find my payment history?", answer: "Navigate to Billing section in your dashboard" },
    { question: "How to contact support?", answer: "Use this support form or email support@company.com" }
  ];

  return (
    <Box sx={{
      maxWidth: 1200,
      mx: 'auto',
      p: 3,
      animation: `${fadeIn} 0.5s ease-out`
    }}>
      {snackbar && (
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar(null)}
          sx={{ mb: 2 }}
        >
          {snackbar.message}
        </Alert>
      )}
      
      <Typography variant="h4" gutterBottom sx={{
        fontWeight: 700,
        mb: 4,
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <SupportAgent fontSize="large" />
        Help & Support Center
      </Typography>

      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        variant="fullWidth"
        sx={{ mb: 4 }}
      >
        <Tab label="New Request" icon={<PendingActions />} />
        <Tab label="My Tickets" icon={<History />} />
        <Tab label="FAQs" icon={<HelpOutline />} />
      </Tabs>

      {tabValue === 0 && (
        <Card sx={{ boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Create New Support Ticket
            </Typography>
             <form onSubmit={handleSubmitTicket}>
            {/* Category Select - REQUIRED FIELD */}
 <div>
  <InputLabel>Issue Category *</InputLabel>
  <Select
    fullWidth
    value={
      ['Technical', 'Billing', 'Account'].includes(formData.category)
        ? formData.category
        : 'Other'
    }
    onChange={(e) => {
      const selected = e.target.value;
      if (selected === 'Other') {
        // Keep the current value if already typed something, or set empty
        setFormData({
          ...formData,
          category:
            ['Technical', 'Billing', 'Account'].includes(formData.category)
              ? ''
              : formData.category, // preserve typed value if already exists
        });
      } else {
        setFormData({ ...formData, category: selected });
      }
    }}
    required
    sx={{ mb: formData.category && !['Technical', 'Billing', 'Account'].includes(formData.category) ? 0 : 2 }}
  >
    <MenuItem value="Technical">Technical Issues</MenuItem>
    <MenuItem value="Billing">Billing & Payments</MenuItem>
    <MenuItem value="Account">Account Management</MenuItem>
    <MenuItem value="Other">Other Issues</MenuItem>
  </Select>

  {/* Conditionally render text field when "Other" is selected */}
  {!['Technical', 'Billing', 'Account'].includes(formData.category) && (
    <TextField
      fullWidth
      required
      label="Please specify your issue"
      value={formData.category}
      onChange={(e) =>
        setFormData({ ...formData, category: e.target.value })
      }
      sx={{ mt: 1, mb: 2 }}
    />
  )}
</div>



            {/* Priority Select */}
            <div>
                <InputLabel>Priority *</InputLabel>
                <Select
                    fullWidth
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    required
                    sx={{mb: 2}}
                >
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                </Select>
            </div>

            {/* Status Select */}
            <div>
                <InputLabel>Status *</InputLabel>
                <Select
                    fullWidth
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    required
                    sx={{mb: 2}}
                >
                    <MenuItem value="OPEN">Open</MenuItem>
                    <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                    <MenuItem value="RESOLVED">Resolved</MenuItem>
                    <MenuItem value="CLOSED">Closed</MenuItem>
                </Select>
            </div>

            {/* Description Field */}
            <div>
                <InputLabel>Description *</InputLabel>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    sx={{mb: 3}}
                />
            </div>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Button
                type="submit"
                variant="contained"
                size="large"
                // disabled={loading}
            >
                {loading ? <CircularProgress size={24} /> : 'Submit Ticket'}
            </Button>
        </form>
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && (
        <ComplaintsTable
          complaints={complaints}
          isLoading={isLoading}
          error={error}
          onRefresh={handleRefresh}
          onViewComplaint={handleViewComplaint}
          onDeleteClick={handleDeleteClick}
          onStatusChange={handleStatusChange}
          updatingStatus={updatingStatus}
        />
      )}

      {tabValue === 2 && (
        <Box>
          {faqItems.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 1, boxShadow: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  <CheckCircle color="primary" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Complaint Detail Modal */}
      <ComplaintDetailModal
        complaint={selectedComplaint}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onStatusChange={handleStatusChange}
        onDeleteClick={handleDeleteClick}
        messages={messages}
        isMessagesLoading={isMessagesLoading}
        updatingStatus={updatingStatus}
        isAdmin={isAdmin}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        complaintId={complaintToDelete}
      />
    </Box>
  );
};

export default HelpSupport;